import React, { useContext } from "react";
import ReactDOM from "react-dom";
import { Route, Switch, Redirect, RouteComponentProps, BrowserRouter } from "react-router-dom";
import { initializeIcons } from "@fluentui/react";

import AppShell from "./components/AppShell/AppShell";

import SLDashboard from "./components/SLDashboard/SLDashboard";

import SLCreate from "./components/SLCreate/SLCreate";

import SLBatch from "./components/SLBatch/SLBatch";
import SLSearch from "./components/SLSearch/SLSearch";

import "./globalCss/Headings.css";
import "./globalCss/Root.css";

import { config } from "./config";
import { SubledgerProvider } from "./contexts/SubledgerContext";
import { UserProvider, UserContext, AuthenticationState } from "./contexts/UserContext";
import { LoggingProvider } from "./contexts/LoggingContext";
import { getLogger } from "./appInsights";
import { getAuthProvider } from "./authProvider";

const Wildcard = () => <Redirect exact from="/" to="/sldashboard" />;

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ILogoutProps extends RouteComponentProps<any> {}

const SLLogout: React.FunctionComponent<ILogoutProps> = () => {
  const { status } = useContext(UserContext);
  if (status === AuthenticationState.Authenticated) {
    return <Redirect to="/sldashboard" />;
  }
  return <p>You have logged out of Subledger successfully. You can close this tab now.</p>;
};

const ContentNotFound = () => {
  return <p>Page not found</p>;
};

const ConfigurationMissingKeys = () => {
  return <p>Configuration has missing settings.</p>;
};

async function main() {
  initializeIcons();
  const envType = config.get("type");
  if (envType !== "accessibility") {
    if (config.get("instrumentationKey") === null) {
      ReactDOM.render(<ConfigurationMissingKeys />, document.getElementById("root"));
      return;
    }
  }
  const script = document.createElement("script");
  script.src = config.get("fdaBotURL") ?? ""; // Use optional chaining to safely access the property
  document.head.appendChild(script);
  const logger = getLogger(envType);
  const authProvider = getAuthProvider(envType);
  try {
    const MainComponent = () => {
      return (
        <BrowserRouter>
          <LoggingProvider appInsights={logger}>
            <UserProvider authProvider={authProvider} mock={envType === "accessibility" || envType === "test"}>
              <SubledgerProvider>
                <AppShell>
                  <Switch>
                    <Route exact path="/logout" component={SLLogout} />
                    <Route path="/sldashboard" component={SLDashboard} />
                    <Route path="/slcreate/:slEntryId?" component={SLCreate} />
                    <Route path="/slbatch/:slBatchRefGUID?" component={SLBatch} />
                    <Route path="/slsearch" component={SLSearch} />
                    <Route exact path="/*" component={Wildcard} />
                    <Route component={ContentNotFound} />
                  </Switch>
                </AppShell>
              </SubledgerProvider>
            </UserProvider>
          </LoggingProvider>
        </BrowserRouter>
      );
    };

    ReactDOM.render(<MainComponent />, document.getElementById("root"));
  } catch (e) {
    console.log(e);
    // something terrible had to happen to get here
    logger.trackException({ exception: e });
  }
}

function polyfillIfNecessary() {
  const missingFeatures = [];
  // these if statements might grow over time
  if (!("finally" in Promise.prototype)) missingFeatures.push("Promise.prototype.finally");
  if (missingFeatures.length === 0) {
    main();
    return;
  }
  const polyfillsUrl = "https://polyfill.io/v3/polyfill.min.js?features=" + missingFeatures.join(",");
  const script = document.createElement("script");
  script.onload = main;
  script.crossOrigin = "anonymous";
  script.integrity = "sha256-k7bQb72oV6zVGAEqEzGc7PqxzOcg+5Q30pSkER9uHDo=";
  script.src = polyfillsUrl;
  document.head.append(script);
}
document.addEventListener("DOMContentLoaded", polyfillIfNecessary, false);
